import { useEffect, useMemo, useRef, useState } from 'react'
import RGL, { WidthProvider, Layout } from 'react-grid-layout'
import { useAppPageLayout } from '../../useAppPageLayout'
import { useGetAppPage } from '../../useGetAppPage'
import { Button } from '@/components/shadcn/button'
import { MAIN_HEADER_HEIGHT, PADDING_GAP } from '../../../config'
import { TContainer } from '../../types'
import { ComponentRenderer } from './ComponentRenderer'
import { TileLayoutInfoDisplay } from './TileLayoutInfoDisplay'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { getPageHeights } from '@/catalog-apps/components/component-library/utils/getPageHeights'

const ReactGridLayout = WidthProvider(RGL)

function toRglLayout(containers: TContainer[]): Layout[] {
  return containers.map((c) => ({
    i: c.id,
    x: c.position.colStart - 1,
    y: c.position.rowStart - 1,
    w: c.position.colSpan,
    h: c.position.rowSpan,
  }))
}

function useRowHeightPx(hasHeader: boolean) {
  const {
    data: { emphasizeHeader },
  } = useGetAppPage()
  const { headerHeight } = getPageHeights(emphasizeHeader ?? false)
  const compute = () => {
    const total = hasHeader
      ? MAIN_HEADER_HEIGHT * 2 + headerHeight + PADDING_GAP * 2
      : MAIN_HEADER_HEIGHT * 2 + PADDING_GAP * 2
    const available = Math.max(0, window.innerHeight - total)
    // Match CSS GRID_ROW_HEIGHT(_NO_HEADER): (available / 2) - (PADDING_GAP / 2)
    return Math.max(20, available / 2 - PADDING_GAP / 2)
  }
  const [rowHeight, setRowHeight] = useState(compute)

  useEffect(() => {
    const handler = () => setRowHeight(compute())
    handler()
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHeader])

  return rowHeight
}

function BuilderTile({ container }: { container: TContainer }) {
  const tileRef = useRef<HTMLDivElement>(null)
  const { position, id: containerId } = container
  const { colStart, colSpan, rowStart, rowSpan } = position
  const { handleDeleteContainer, numberOfContainers } = useAppPageLayout()
  const isPageBuilder = useIsPageBuilder()
  return (
    <div
      ref={tileRef}
      className="relative flex size-full rounded-md border border-dashed border-foreground bg-background/80 shadow-lg"
    >
      <div className="tile-drag-handle absolute left-0 right-0 top-0 h-6 cursor-move bg-secondary/40" />
      {isPageBuilder && numberOfContainers > 1 && (
        <Button
          size="icon"
          variant="outline"
          className="absolute -right-2 -top-1 z-30 h-7 w-7 rounded-full"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteContainer(containerId)
          }}
        >
          <IconCloseOutline className="h-4 w-4" />
        </Button>
      )}
      <ComponentRenderer component={container.component} containerId={containerId} tileContainerRef={tileRef} />
      <TileLayoutInfoDisplay rowStart={rowStart} colStart={colStart} rowSpan={rowSpan} colSpan={colSpan} />
    </div>
  )
}

export function TileGridBuilderRGL() {
  const { data } = useGetAppPage()
  const { pageFilters, title } = data
  const hasHeader = !!title || pageFilters.length > 0

  const { containers, gridCols, gridRows, applyLayout } = useAppPageLayout()
  const layout = useMemo(() => toRglLayout(containers), [containers])
  const rowHeight = useRowHeightPx(hasHeader)
  const minHeight = useMemo(() => gridRows * rowHeight + Math.max(0, gridRows - 1) * PADDING_GAP, [gridRows, rowHeight])

  const clampToGrid = (items: Layout[]): Layout[] => {
    return items.map((l) => {
      let x = Math.max(0, l.x)
      let y = Math.max(0, l.y)
      let w = Math.max(1, l.w)
      let h = Math.max(1, l.h)
      if (x + w > gridCols) {
        if (w > gridCols) w = gridCols
        x = Math.min(x, Math.max(0, gridCols - w))
      }
      if (y + h > gridRows) {
        if (h > gridRows) h = gridRows
        y = Math.min(y, Math.max(0, gridRows - h))
      }
      return { ...l, x, y, w, h }
    })
  }

  const onDrag = () => {}
  const onResize = () => {}
  const rectsOverlap = (a: Layout, b: Layout) =>
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y

  // helper retained for future behaviors; currently unused because preventCollision handles this

  const onDragStop = (
    next: Layout[],
    oldItem: { i: string; x: number; y: number; w: number; h: number },
    newItem: { i: string; x: number; y: number; w: number; h: number }
  ) => {
    const others = next.filter((l) => l.i !== newItem.i)
    const hasCollision = others.some((l) => rectsOverlap(newItem as Layout, l))
    const layoutCopy = next.map((l) => ({ ...l }))
    const newRef = layoutCopy.find((l) => l.i === newItem.i)!
    if (hasCollision) {
      // Revert to original position if space is not empty
      newRef.x = oldItem.x
      newRef.y = oldItem.y
      newRef.w = oldItem.w
      newRef.h = oldItem.h
    }
    applyLayout(clampToGrid(layoutCopy))
  }
  const onResizeStop = (next: Layout[]) => applyLayout(clampToGrid(next))

  return (
    <ReactGridLayout
      cols={gridCols}
      layout={layout}
      rowHeight={rowHeight}
      maxRows={gridRows}
      style={{ minHeight }}
      margin={[PADDING_GAP, PADDING_GAP]}
      // containerPadding={[PADDING_GAP, PADDING_GAP]}
      containerPadding={[0, 0]}
      compactType={null}
      preventCollision
      isBounded
      draggableHandle=".tile-drag-handle"
      resizeHandles={['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw']}
      onDrag={onDrag}
      onResize={onResize}
      onDragStop={onDragStop}
      onResizeStop={onResizeStop}
      // Ensure RGL stretches full width like the previous grid
      className="w-full"
    >
      {containers.map((container) => {
        return (
          <div key={container.id}>
            <BuilderTile container={container} />
          </div>
        )
      })}
    </ReactGridLayout>
  )
}
